import React, { useEffect, useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Store, Share2, Copy, ExternalLink, LogOut, Phone, MapPin, QrCode, Download, X, Truck, ChevronRight, Globe, Percent, Info, Banknote, CreditCard } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import api, { apiError, fileUrl } from "@/lib/api";
import { money, COUNTRIES, PAY_PRESETS, CURRENCIES } from "@/lib/orderStatus";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { Brand } from "@/components/Brand";
import PhoneVerify from "@/components/PhoneVerify";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { SalesPolicyCard } from "@/components/settings/SalesPolicyCard";
import { SlugEditor } from "@/components/settings/SlugEditor";
import { StripeConnectCard } from "@/components/settings/StripeConnectCard";
import i18n, { setLanguage } from "@/i18n";

let _rowKey = 0;
const nextKey = () => `row-${++_rowKey}`;

// Transporteurs disponibles par pays (grilles tarifaires publiques intégrées côté backend)
// soon:true => affiché mais non sélectionnable (API pas encore branchée, ex. Uber Direct)
const CARRIERS_BY_COUNTRY = {
  CA: [
    { id: "poste_canada", label: "Poste Canada", desc: "Calcul automatique selon le poids", auto: true },
    { id: "uber_direct", label: "Uber Direct", desc: "Livraison à la demande — bientôt disponible", soon: true },
  ],
  FR: [
    { id: "colissimo", label: "Colissimo (La Poste)", desc: "Calcul automatique selon le poids", auto: true },
    { id: "uber_direct", label: "Uber Direct", desc: "Livraison à la demande — bientôt disponible", soon: true },
  ],
};

// Placeholders adaptés selon le moyen de paiement (guide le marchand)
const METHOD_PLACEHOLDERS = {
  paypal: "Votre lien PayPal.me (ex : https://paypal.me/votreboutique)",
  carte: "Votre lien de paiement Stripe (https://buy.stripe.com/...)",
  interac: "Email ou téléphone Interac (dépôt automatique)",
  virement: "Votre IBAN / RIB",
  lydia: "Votre numéro ou lien Lydia/Paylib",
};

// Guides d'aide pas-à-pas affichés sous chaque moyen de paiement
const METHOD_HELP = {
  paypal: {
    steps: "Ouvrez paypal.me, créez votre lien (ex. paypal.me/votreboutique), puis collez-le ci-dessus.",
    link: { url: "https://www.paypal.com/paypalme/", label: "Créer mon lien PayPal.me" },
  },
  carte: {
    steps: "Dans Stripe → Paiements → Liens de paiement → « Créer », choisissez un montant ou « le client saisit le montant », puis copiez le lien ici.",
    link: { url: "https://dashboard.stripe.com/payment-links", label: "Créer mon lien de paiement Stripe" },
  },
  interac: {
    steps: "Indiquez l'email ou le téléphone de votre compte Interac. Activez le « dépôt automatique » dans votre appli bancaire pour recevoir les virements sans question de sécurité.",
    link: { url: "https://www.interac.ca/fr/particuliers/utiliser-virement-interac/", label: "En savoir plus sur Interac" },
  },
};


export default function Settings() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { data: business } = useQuery({ queryKey: ["business"], queryFn: async () => (await api.get("/business")).data });
  const [form, setForm] = useState({ name: "", description: "", whatsapp: "", city: "" });
  const [zones, setZones] = useState([]);
  const [methods, setMethods] = useState([]);
  const [currency, setCurrency] = useState("XOF");
  const [taxes, setTaxes] = useState([]);
  const [shippingBase, setShippingBase] = useState("");
  const [shippingPerKg, setShippingPerKg] = useState("");
  const [shippingCarrier, setShippingCarrier] = useState("");
  const [shippingMargin, setShippingMargin] = useState("");
  const [carriers, setCarriers] = useState([]);
  const [interac, setInterac] = useState({ enabled: false, handle: "" });
  const [billing, setBilling] = useState(null);
  const [chargingNow, setChargingNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [waVerified, setWaVerified] = useState(false);
  const [multilingual, setMultilingual] = useState(false);
  const qrRef = useRef(null);

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `boutique-${business?.slug || "qr"}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success(t("settings.qrDownloaded"));
  };

  useEffect(() => {
    if (business) {
      setForm({ name: business.name || "", description: business.description || "", whatsapp: business.whatsapp || "", city: business.city || "" });
      setWaVerified(!!business.whatsapp_verified);
      setMultilingual(!!business.multilingual);
      if (business.language && business.language !== i18n.language) setLanguage(business.language);
      setZones((business.delivery_zones || []).map((z) => ({ ...z, _k: nextKey() })));
      const ctry = business.country || "CI";
      setCurrency(business.currency || COUNTRIES[ctry]?.currency || "XOF");
      const defaultTaxes = ctry === "CA"
        ? [{ name: "TPS", rate: 5 }, { name: "TVQ", rate: 9.975 }]
        : (COUNTRIES[ctry]?.tax != null ? [{ name: "TVA", rate: COUNTRIES[ctry].tax }] : []);
      if (business.taxes !== undefined && business.taxes !== null) {
        setTaxes((business.taxes || []).map((t) => ({ _k: nextKey(), name: t.name, rate: +(t.rate * 100).toFixed(3) })));
      } else if (business.tax_rate != null) {
        setTaxes([{ _k: nextKey(), name: business.tax_name || "TVA", rate: +(business.tax_rate * 100).toFixed(3) }]);
      } else {
        setTaxes(defaultTaxes.map((x) => ({ ...x, _k: nextKey() })));
      }
      setShippingBase(business.shipping_base != null ? String(business.shipping_base) : "7.99");
      setShippingPerKg(business.shipping_per_kg != null ? String(business.shipping_per_kg) : "2.5");
      setShippingCarrier(business.shipping_carrier ?? (CARRIERS_BY_COUNTRY[business.country]?.[0]?.id || ""));
      setShippingMargin(business.shipping_margin != null ? String(business.shipping_margin) : "0");
      setCarriers((business.shipping_carriers || []).map((c) => ({ _k: nextKey(), name: c.name || "", base: c.base ?? "", per_kg: c.per_kg ?? "" })));
      setInterac({ enabled: !!business.interac_enabled, handle: business.interac_handle || "" });
      // Charge les moyens de paiement (ou migre l'ancien format payment_config)
      if (business.payment_methods?.length) {
        setMethods(business.payment_methods);
      } else {
        const pc = business.payment_config || {};
        const migrated = ["wave", "mtn", "orange"]
          .filter((k) => pc[k]?.number || pc[k]?.qr_url)
          .map((k) => ({ id: k, label: PAY_PRESETS[k]?.label || k, color: PAY_PRESETS[k]?.color || "#E85D04", holder: "", number: pc[k].number || "", qr_url: pc[k].qr_url || "" }));
        setMethods(migrated);
      }
    }
  }, [business]);

  useEffect(() => {
    if (business?.country === "CA") {
      api.get("/billing/status").then(({ data }) => setBilling(data)).catch(() => {});
      const params = new URLSearchParams(window.location.search);
      if (params.get("billing") === "success") {
        toast.success(t("settings.cardSaved"));
        window.history.replaceState({}, "", window.location.pathname);
        setTimeout(() => api.get("/billing/status").then(({ data }) => setBilling(data)).catch(() => {}), 1500);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.country]);

  const saveCard = async () => {
    try {
      const { data } = await api.post("/billing/setup-checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };
  const chargeNow = async () => {
    if (!window.confirm(t("settings.chargeConfirm", { amount: billing?.owed, currency: billing?.currency }))) return;
    setChargingNow(true);
    try {
      const { data } = await api.post("/billing/charge-now");
      toast.success(t("settings.chargeDone", { amount: data.charged, currency: (data.currency || "").toUpperCase() }));
      const { data: st } = await api.get("/billing/status");
      setBilling(st);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setChargingNow(false);
    }
  };

  const country = business?.country || "CI";
  const isFormula = business?.logistics_mode === "formula" || ["CA", "FR"].includes(country);
  const carrierRegion = ["CA", "FR"].includes(country); // transporteurs gérés dans l'onglet dédié
  const storeUrl = business ? `${window.location.origin}/boutique/${business.slug}` : "";

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, delivery_zones: zones, payment_methods: methods, currency };
      payload.taxes = taxes
        .filter((t) => (t.name || "").trim())
        .map((t) => ({ name: t.name.trim(), rate: (parseFloat(t.rate) || 0) / 100 }));
      if (isFormula && !carrierRegion) {
        payload.shipping_carrier = shippingCarrier || "";
        payload.shipping_margin = parseFloat(shippingMargin) || 0;
        if (!shippingCarrier) {
          payload.shipping_base = parseFloat(shippingBase) || 0;
          payload.shipping_per_kg = parseFloat(shippingPerKg) || 0;
        }
        payload.shipping_carriers = carriers
          .filter((c) => (c.name || "").trim())
          .map((c) => ({ name: c.name.trim(), base: parseFloat(c.base) || 0, per_kg: parseFloat(c.per_kg) || 0 }));
      }
      if (country === "CA") {
        payload.interac_enabled = interac.enabled;
        payload.interac_handle = interac.handle.trim();
      }
      await api.put("/business", payload);
      qc.invalidateQueries({ queryKey: ["business"] });
      toast.success(t("settings.storeUpdated"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const presetSelected = (id) => methods.some((m) => m.id === id);
  const togglePreset = (id) => {
    if (presetSelected(id)) { setMethods(methods.filter((m) => m.id !== id)); return; }
    if (methods.length >= 3) { toast.error(t("settings.max3pay")); return; }
    const p = PAY_PRESETS[id] || { label: id, color: "#E85D04" };
    setMethods([...methods, { id, label: p.label, color: p.color, holder: "", number: "", qr_url: "" }]);
  };
  const addCustom = () => {
    if (methods.length >= 3) { toast.error(t("settings.max3pay")); return; }
    const name = window.prompt(t("settings.customMethodPrompt"));
    if (!name || !name.trim()) return;
    setMethods([...methods, { id: `custom_${Date.now()}`, label: name.trim(), color: "#E85D04", holder: "", number: "", qr_url: "" }]);
  };
  const updMethod = (id, key, val) => setMethods((ms) => ms.map((m) => (m.id === id ? { ...m, [key]: val } : m)));
  const rmMethod = (id) => setMethods((ms) => ms.filter((m) => m.id !== id));

  const addTax = () => setTaxes([...taxes, { _k: nextKey(), name: "", rate: "" }]);
  const updTax = (i, k, v) => setTaxes(taxes.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const rmTax = (i) => setTaxes(taxes.filter((_, idx) => idx !== i));

  const addZone = () => setZones([...zones, { _k: nextKey(), name: "", fee: 0 }]);
  const updZone = (i, k, v) => { const c = [...zones]; c[i][k] = k === "fee" ? +v : v; setZones(c); };
  const rmZone = (i) => setZones(zones.filter((_, idx) => idx !== i));
  // Transporteurs (CA/FR) gérés dans l'onglet dédié /transporteurs

  const copy = () => { navigator.clipboard.writeText(storeUrl); toast.success(t("settings.linkCopied")); };
  const share = () => {
    const msg = `Découvrez ma boutique ${form.name} en ligne 🛍️ : ${storeUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="px-4 pt-6">
      <h1 className="font-display text-2xl font-extrabold tracking-tight mb-1">{t("settings.title")}</h1>
      <p className="text-sm text-[#5C574F] mb-5">
        {t("settings.configureStore")} • <span className="font-semibold text-[#1B4332]">{COUNTRIES[country]?.flag} {COUNTRIES[country]?.name}</span> ({currency}) • <Brand country={country} className="text-[#1B4332]" />
      </p>

      {/* Langue de l'application */}
      <div className="bg-white rounded-2xl p-4 mb-5 border border-black/5 flex items-center justify-between gap-3" data-testid="language-card">
        <div className="flex items-center gap-2.5 min-w-0">
          <Globe className="h-5 w-5 text-[#1B4332] shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold text-[#1A1A1A] text-sm">{t("settings.languageTitle")}</p>
            <p className="text-xs text-[#9c968a]">{t("settings.languageHint")}</p>
          </div>
        </div>
        <LanguageSwitcher onChange={(lng) => api.put("/business", { language: lng }).catch(() => {})} />
      </div>

      {/* Boutique multilingue : sélecteur de langue côté vitrine */}
      <div className="bg-white rounded-2xl p-4 mb-5 border border-black/5 flex items-center justify-between gap-3" data-testid="multilingual-card">
        <div className="flex items-center gap-2.5 min-w-0">
          <Globe className="h-5 w-5 text-[#0077B6] shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold text-[#1A1A1A] text-sm">{t("settings.multilingualTitle")}</p>
            <p className="text-xs text-[#9c968a]">{t("settings.multilingualHint")}</p>
          </div>
        </div>
        <button
          type="button"
          data-testid="multilingual-toggle"
          role="switch"
          aria-checked={multilingual}
          onClick={() => { const v = !multilingual; setMultilingual(v); api.put("/business", { multilingual: v }).then(() => qc.invalidateQueries({ queryKey: ["business"] })).catch(() => {}); }}
          className={`relative h-7 w-12 rounded-full transition-colors shrink-0 ${multilingual ? "bg-[#0077B6]" : "bg-[#E5E0D8]"}`}
        >
          <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${multilingual ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
      </div>

      {/* Commission plateforme */}
      <div className="bg-white rounded-2xl p-4 mb-5 border border-black/5 flex items-center gap-3" data-testid="commission-card">
        <span className="h-10 w-10 rounded-xl bg-[#E85D04]/10 text-[#E85D04] flex items-center justify-center shrink-0"><Percent className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-[#1A1A1A] text-sm">Commission plateforme : 2,5 %</p>
          <p className="text-xs text-[#9c968a]">Prélevée sur les ventes encaissées via la plateforme (non remboursable).</p>
        </div>
        <a href="/cgv" target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#E85D04] shrink-0" data-testid="commission-cgv-link">Détails</a>
      </div>

      {/* One-pager card */}
      <div className="bg-[#1B4332] rounded-2xl p-5 text-white mb-5 relative overflow-hidden" data-testid="onepager-card">
        <div className="absolute inset-0 fabric-grain opacity-20" />
        <div className="relative">
          <div className="flex items-center gap-2 text-sm font-medium mb-2"><Store className="h-4 w-4" /> {t("settings.onepagerTitle")}</div>
          <p className="text-xs text-white/70 break-all mb-3" data-testid="store-url">{storeUrl}</p>
          <div className="grid grid-cols-3 gap-2">
            <button data-testid="copy-link-btn" onClick={copy} className="h-11 rounded-xl bg-white/15 flex items-center justify-center gap-1 text-sm font-semibold"><Copy className="h-4 w-4" /> {t("settings.copy")}</button>
            <button data-testid="share-link-btn" onClick={share} className="h-11 rounded-xl bg-[#25D366] flex items-center justify-center gap-1 text-sm font-semibold"><Share2 className="h-4 w-4" /> {t("settings.share")}</button>
            <a data-testid="open-store-btn" href={storeUrl} target="_blank" rel="noreferrer" className="h-11 rounded-xl bg-[#FFBA08] text-[#1A1A1A] flex items-center justify-center gap-1 text-sm font-semibold"><ExternalLink className="h-4 w-4" /> {t("settings.view")}</a>
          </div>
          <button data-testid="show-qr-btn" onClick={() => setShowQR(true)} className="w-full h-11 mt-2 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center gap-2 text-sm font-semibold"><QrCode className="h-4 w-4" /> {t("settings.showQr")}</button>
        </div>
      </div>

      <SlugEditor business={business} onSaved={() => qc.invalidateQueries({ queryKey: ["business"] })} />

      {!["CA", "FR"].includes(country) && (
        <button data-testid="manage-drivers-btn" onClick={() => navigate("/livreurs")} className="w-full bg-white rounded-2xl border border-black/5 shadow-sm p-4 mb-5 flex items-center gap-3 active:scale-[0.99] transition-transform">
          <span className="h-10 w-10 rounded-full bg-[#0077B6]/10 text-[#0077B6] flex items-center justify-center"><Truck className="h-5 w-5" /></span>
          <span className="flex-1 text-left">
            <span className="block font-semibold text-sm">{t("settings.myDrivers")}</span>
            <span className="block text-xs text-[#9c968a]">{t("settings.driversSub")}</span>
          </span>
          <ChevronRight className="h-5 w-5 text-[#cfc8ba]" />
        </button>
      )}

      <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 space-y-4">
        <Field label={t("settings.bizName")} testid="biz-name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" /></Field>
        <Field label={t("settings.description")}><textarea data-testid="biz-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full bg-[#F9F6F0] rounded-xl p-3 outline-none border border-transparent focus:border-[#E85D04] resize-none" /></Field>
        <Field label={t("settings.whatsappNum")} icon={Phone}><input data-testid="biz-whatsapp" value={form.whatsapp} onChange={(e) => { setForm({ ...form, whatsapp: e.target.value }); setWaVerified(false); }} placeholder={t("settings.whatsappPh")} className="input pl-10" /></Field>
        <PhoneVerify phone={form.whatsapp} verified={waVerified} onVerified={() => setWaVerified(true)} hint={t("settings.whatsappHint")} country={country} />
        <Field label={t("settings.city")} icon={MapPin}><input data-testid="biz-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input pl-10" /></Field>
        <Field label={t("settings.currency")}>
          <select data-testid="biz-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className="input appearance-none cursor-pointer">
            {CURRENCIES.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
        </Field>
        <div>
          <label className="block text-sm font-semibold text-[#5C574F] mb-1.5">{t("settings.taxesApplied")}</label>
          <p className="text-xs text-[#9c968a] mb-2">{t("settings.taxesHint")}</p>
          {taxes.map((tx, i) => (
            <div key={tx._k} className="flex gap-2 items-center mb-2" data-testid={`tax-row-${i}`}>
              <input data-testid={`tax-name-${i}`} value={tx.name} onChange={(e) => updTax(i, "name", e.target.value)} placeholder={t("settings.taxNamePh")} className="flex-1 input" />
              <div className="relative w-28">
                <input data-testid={`tax-rate-${i}`} type="number" step="0.001" value={tx.rate} onChange={(e) => updTax(i, "rate", e.target.value)} placeholder={t("settings.taxRatePh")} className="input pr-7" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9c968a] text-sm">%</span>
              </div>
              <button type="button" data-testid={`tax-remove-${i}`} onClick={() => rmTax(i)} className="h-12 w-12 rounded-xl bg-[#D00000]/10 text-[#D00000] flex items-center justify-center shrink-0">✕</button>
            </div>
          ))}
          {taxes.length === 0 && <p className="text-xs text-[#9c968a] mb-2">{t("settings.noTaxes")}</p>}
          <button type="button" data-testid="add-tax-btn" onClick={addTax} className="w-full h-11 mt-1 rounded-xl border border-dashed border-[#E5E0D8] text-[#5C574F] font-medium text-sm">{t("settings.addTax")}</button>
        </div>
        <button data-testid="save-biz-btn" onClick={save} disabled={busy} className="w-full h-14 rounded-2xl bg-[#E85D04] text-white font-bold active:scale-95 transition-transform disabled:opacity-60">
          {busy ? t("settings.saving") : t("settings.save")}
        </button>
      </div>

      {["CA", "FR"].includes(business?.country) && <StripeConnectCard />}

      <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 mt-4" data-testid="payment-config-card">
        <h3 className="font-display font-semibold mb-1 flex items-center gap-2"><QrCode className="h-5 w-5 text-[#E85D04]" /> {t("settings.payMethodsTitle")}</h3>
        <p className="text-xs text-[#9c968a] mb-4">{t("settings.payMethodsHint")}</p>

        <div className="flex flex-wrap gap-2 mb-4">
          {(COUNTRIES[country]?.presets || []).map((id) => {
            const p = PAY_PRESETS[id] || { label: id, color: "#E85D04" };
            const sel = presetSelected(id);
            return (
              <button
                key={id}
                type="button"
                data-testid={`preset-${id}`}
                onClick={() => togglePreset(id)}
                className={`h-9 px-3 rounded-full text-sm font-semibold transition-colors border ${sel ? "text-white border-transparent" : "bg-[#F9F6F0] text-[#5C574F] border-[#E5E0D8]"}`}
                style={sel ? { background: p.color } : {}}
              >
                {sel ? "✓ " : ""}{p.label}
              </button>
            );
          })}
          <button type="button" data-testid="add-custom-method" onClick={addCustom} className="h-9 px-3 rounded-full text-sm font-semibold border border-dashed border-[#E5E0D8] text-[#5C574F]">{t("settings.other")}</button>
        </div>

        <div className="space-y-4">
          {methods.map((m) => (
            <div key={m.id} data-testid={`paymethod-${m.id}`} className="border border-[#F0ece4] rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-sm" style={{ color: m.color }}>{m.label}</span>
                <button type="button" data-testid={`remove-method-${m.id}`} onClick={() => rmMethod(m.id)} className="h-7 w-7 rounded-lg bg-[#D00000]/10 text-[#D00000] flex items-center justify-center text-sm">✕</button>
              </div>
              <div className="flex gap-3 items-start">
                <QrUpload svc={m.id} url={m.qr_url} onUploaded={(u) => updMethod(m.id, "qr_url", u)} />
                <div className="flex-1 space-y-2">
                  <input
                    data-testid={`method-holder-${m.id}`}
                    value={m.holder || ""}
                    onChange={(e) => updMethod(m.id, "holder", e.target.value)}
                    placeholder={t("settings.holderPh")}
                    className="w-full h-11 bg-[#F9F6F0] rounded-xl px-3 outline-none border border-transparent focus:border-[#E85D04] text-sm"
                  />
                  <input
                    data-testid={`method-number-${m.id}`}
                    value={m.number || ""}
                    onChange={(e) => updMethod(m.id, "number", e.target.value)}
                    placeholder={METHOD_PLACEHOLDERS[m.id] || t("settings.numberPh")}
                    className="w-full h-11 bg-[#F9F6F0] rounded-xl px-3 outline-none border border-transparent focus:border-[#E85D04] text-sm"
                  />
                </div>
              </div>
              {METHOD_HELP[m.id] && (
                <div data-testid={`method-help-${m.id}`} className="mt-2 flex items-start gap-2 rounded-lg bg-[#F0F7F4] border border-[#1B4332]/10 p-2.5">
                  <Info className="h-4 w-4 text-[#1B4332] shrink-0 mt-0.5" />
                  <div className="text-xs text-[#5C574F] leading-relaxed">
                    {METHOD_HELP[m.id].steps}
                    <a href={METHOD_HELP[m.id].link.url} target="_blank" rel="noreferrer" data-testid={`method-help-link-${m.id}`} className="mt-1 inline-flex items-center gap-1 font-semibold text-[#1B4332] hover:text-[#E85D04] transition-colors">
                      <ExternalLink className="h-3.5 w-3.5" /> {METHOD_HELP[m.id].link.label}
                    </a>
                  </div>
                </div>
              )}
            </div>
          ))}
          {methods.length === 0 && <p className="text-xs text-[#9c968a]">{t("settings.noMethods")}</p>}
        </div>
        <button onClick={save} disabled={busy} data-testid="save-paycfg-btn" className="w-full h-12 mt-4 rounded-2xl bg-[#1B4332] text-white font-semibold active:scale-95 transition-transform disabled:opacity-60">{t("settings.savePayment")}</button>
      </div>

      {!isFormula && (
        <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 mt-4" data-testid="zones-card">
          <h3 className="font-display font-semibold mb-1">{t("settings.zonesTitle")}</h3>
          <p className="text-xs text-[#9c968a] mb-3">{t("settings.zonesHint")}</p>
          <div className="space-y-2">
            {zones.map((z, i) => (
              <div key={z._k} className="flex gap-2 items-center" data-testid={`zone-row-${i}`}>
                <input value={z.name} onChange={(e) => updZone(i, "name", e.target.value)} placeholder={t("settings.zoneNamePh")} className="flex-1 h-12 bg-[#F9F6F0] rounded-xl px-3 outline-none border border-transparent focus:border-[#E85D04]" data-testid={`zone-name-${i}`} />
                <input type="number" value={z.fee} onChange={(e) => updZone(i, "fee", e.target.value)} placeholder={t("settings.feePh")} className="w-28 h-12 bg-[#F9F6F0] rounded-xl px-3 outline-none border border-transparent focus:border-[#E85D04]" data-testid={`zone-fee-${i}`} />
                <button onClick={() => rmZone(i)} className="h-12 w-12 rounded-xl bg-[#D00000]/10 text-[#D00000] flex items-center justify-center shrink-0" data-testid={`zone-remove-${i}`}>✕</button>
              </div>
            ))}
          </div>
          <button onClick={addZone} data-testid="add-zone-btn" className="w-full h-11 mt-3 rounded-xl border border-dashed border-[#E5E0D8] text-[#5C574F] font-medium text-sm">{t("settings.addZone")}</button>
          <button onClick={save} disabled={busy} data-testid="save-zones-btn" className="w-full h-12 mt-3 rounded-2xl bg-[#1B4332] text-white font-semibold active:scale-95 transition-transform disabled:opacity-60">{t("settings.saveZones")}</button>
        </div>
      )}

      {isFormula && !carrierRegion && (
        <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 mt-4" data-testid="shipping-formula-card">
          <h3 className="font-display font-semibold mb-1 flex items-center gap-2"><Truck className="h-5 w-5 text-[#0077B6]" /> {t("settings.carriersTitle")}</h3>
          <p className="text-xs text-[#9c968a] mb-4">{t("settings.carriersHint")}</p>
          <div className="space-y-2">
            {(CARRIERS_BY_COUNTRY[country] || []).map((c) => {
              const selected = shippingCarrier === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  disabled={c.soon}
                  data-testid={`carrier-option-${c.id}`}
                  onClick={() => !c.soon && setShippingCarrier(c.id)}
                  className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${c.soon ? "opacity-50 cursor-not-allowed border-[#E5E0D8] bg-[#F9F6F0]" : selected ? "border-[#1B4332] bg-[#F0F7F4]" : "border-[#E5E0D8] bg-white hover:border-[#1B4332]/40"}`}
                >
                  <span className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? "border-[#1B4332]" : "border-[#cfc8ba]"}`}>{selected && <span className="h-2.5 w-2.5 rounded-full bg-[#1B4332]" />}</span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2 font-semibold text-sm">{c.label}{c.soon && <span className="text-[10px] font-bold uppercase tracking-wide text-[#E85D04] bg-[#E85D04]/10 px-1.5 py-0.5 rounded">{t("settings.carrierSoon")}</span>}</span>
                    <span className="block text-xs text-[#9c968a]">{c.desc}</span>
                  </span>
                </button>
              );
            })}
            <button
              type="button"
              data-testid="carrier-option-custom"
              onClick={() => setShippingCarrier("")}
              className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${shippingCarrier === "" ? "border-[#1B4332] bg-[#F0F7F4]" : "border-[#E5E0D8] bg-white hover:border-[#1B4332]/40"}`}
            >
              <span className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${shippingCarrier === "" ? "border-[#1B4332]" : "border-[#cfc8ba]"}`}>{shippingCarrier === "" && <span className="h-2.5 w-2.5 rounded-full bg-[#1B4332]" />}</span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-sm">{t("settings.carrierCustom")}</span>
                <span className="block text-xs text-[#9c968a]">{t("settings.carrierCustomDesc")}</span>
              </span>
            </button>
          </div>
          {shippingCarrier ? (
            <>
              <div data-testid="carrier-note" className="mt-3 flex items-start gap-2 rounded-lg bg-[#F0F7F4] border border-[#1B4332]/10 p-2.5">
                <Info className="h-4 w-4 text-[#1B4332] shrink-0 mt-0.5" />
                <p className="text-xs text-[#5C574F] leading-relaxed">{t("settings.carrierNote")}</p>
              </div>
              <div className="mt-3">
                <Field label={t("settings.carrierMargin", { cur: currency })}><input data-testid="shipping-margin" type="number" step="0.01" value={shippingMargin} onChange={(e) => setShippingMargin(e.target.value)} placeholder="0" className="input" /></Field>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label={t("settings.baseRate", { cur: currency })}><input data-testid="shipping-base" type="number" step="0.01" value={shippingBase} onChange={(e) => setShippingBase(e.target.value)} placeholder="7.99" className="input" /></Field>
                <Field label={t("settings.perKg", { cur: currency })}><input data-testid="shipping-per-kg" type="number" step="0.01" value={shippingPerKg} onChange={(e) => setShippingPerKg(e.target.value)} placeholder="2.5" className="input" /></Field>
              </div>
              <p className="text-[11px] text-[#9c968a] mt-2">{t("settings.shippingExample", { amount: money((parseFloat(shippingBase) || 0) + 2 * (parseFloat(shippingPerKg) || 0), currency) })}</p>
            </>
          )}
          <button onClick={save} disabled={busy} data-testid="save-shipping-btn" className="w-full h-12 mt-4 rounded-2xl bg-[#1B4332] text-white font-semibold active:scale-95 transition-transform disabled:opacity-60">{t("settings.saveShipping")}</button>
        </div>
      )}

      <SalesPolicyCard />

      {country === "CA" && (
        <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 mt-4" data-testid="interac-card">
          <h3 className="font-display font-semibold mb-1 flex items-center gap-2"><Banknote className="h-5 w-5 text-[#E85D04]" /> {t("settings.interacTitle")}</h3>
          <p className="text-xs text-[#9c968a] mb-3">{t("settings.interacHint")}</p>
          <label className="flex items-center justify-between gap-3 mb-3 cursor-pointer">
            <span className="text-sm font-medium">{t("settings.interacEnable")}</span>
            <input type="checkbox" data-testid="interac-enable-toggle" checked={interac.enabled} onChange={(e) => setInterac({ ...interac, enabled: e.target.checked })} className="h-5 w-9 accent-[#E85D04]" />
          </label>
          {interac.enabled && (
            <Field label={t("settings.interacHandle")}>
              <input data-testid="interac-handle" value={interac.handle} onChange={(e) => setInterac({ ...interac, handle: e.target.value })} placeholder="paiement@maboutique.com ou +1 514 555 1234" className="input" />
            </Field>
          )}
          {billing && billing.count > 0 && (
            <div className="mt-3 rounded-xl bg-[#FFBA08]/12 p-3 text-sm">
              <p className="text-[#5C574F]">{t("settings.interacCommission", { count: billing.count })}</p>
              <p className="font-display font-bold text-[#E85D04] text-lg">{billing.owed} {billing.currency}</p>
            </div>
          )}
          <button onClick={save} disabled={busy} data-testid="save-interac-btn" className="w-full h-12 mt-3 rounded-2xl bg-[#E85D04] text-white font-semibold active:scale-95 transition-transform disabled:opacity-60">{t("settings.saveShipping")}</button>
        </div>
      )}

      {country === "CA" && (
        <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 mt-4" data-testid="billing-card">
          <h3 className="font-display font-semibold mb-1 flex items-center gap-2"><CreditCard className="h-5 w-5 text-[#1B4332]" /> {t("settings.billingTitle")}</h3>
          <p className="text-xs text-[#9c968a] mb-3">{t("settings.billingHint")}</p>
          {billing && billing.count > 0 && (
            <div className="mb-3 rounded-xl bg-[#FFBA08]/12 p-3 text-sm" data-testid="commission-owed">
              <p className="text-[#5C574F]">{t("settings.interacCommission", { count: billing.count })}</p>
              <p className="font-display font-bold text-[#E85D04] text-lg">{billing.owed} {billing.currency}</p>
            </div>
          )}
          {billing?.has_pm ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm bg-[#F9F6F0] rounded-xl px-4 h-12" data-testid="billing-pm">
                <CreditCard className="h-4 w-4 text-[#5C574F]" />
                <span className="font-medium capitalize">{billing.brand}</span>
                <span className="text-[#5C574F]">•••• {billing.last4}</span>
                <button onClick={saveCard} className="ml-auto text-xs text-[#1B4332] underline" data-testid="change-card-btn">{t("settings.changeCard")}</button>
              </div>
              <p className="text-xs text-[#9c968a]">{t("settings.autoChargeNote")}</p>
              {billing.count > 0 && (
                <button onClick={chargeNow} disabled={chargingNow} data-testid="charge-now-btn" className="w-full h-12 rounded-2xl bg-[#1B4332] text-white font-semibold active:scale-95 transition-transform disabled:opacity-60">
                  {chargingNow ? t("common.sending") : t("settings.chargeNow", { amount: billing.owed, currency: billing.currency })}
                </button>
              )}
            </div>
          ) : (
            <button onClick={saveCard} data-testid="save-card-btn" className="w-full h-12 rounded-2xl bg-[#1B4332] text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform">
              <CreditCard className="h-5 w-5" /> {t("settings.saveCard")}
            </button>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-black/5 shadow-sm p-4 mt-4 flex items-center justify-between">
        <div>
          <p className="font-semibold text-sm">{user?.name}</p>
          <p className="text-xs text-[#9c968a]">{user?.email}</p>
        </div>
        <button data-testid="logout-btn" onClick={logout} className="h-11 px-4 rounded-xl bg-[#D00000]/10 text-[#D00000] font-semibold text-sm flex items-center gap-1.5"><LogOut className="h-4 w-4" /> {t("settings.logout")}</button>
      </div>

      <style>{`.input{width:100%;height:3rem;background:#F9F6F0;border-radius:0.85rem;padding:0 0.75rem;outline:none;border:1px solid transparent}.input:focus{border-color:#E85D04}`}</style>

      {showQR && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowQR(false)} data-testid="qr-modal">
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm text-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-extrabold">{t("settings.qrModalTitle")}</h3>
              <button onClick={() => setShowQR(false)} className="h-9 w-9 rounded-full bg-[#F9F6F0] flex items-center justify-center"><X className="h-5 w-5" /></button>
            </div>
            <div ref={qrRef} className="bg-white inline-block p-4 rounded-2xl border border-[#E5E0D8] mb-3" data-testid="qr-canvas-wrap">
              <QRCodeCanvas value={storeUrl} size={220} level="M" marginSize={2} fgColor="#1B4332" />
            </div>
            <p className="font-semibold text-sm text-[#1A1A1A]">{form.name}</p>
            <p className="text-xs text-[#9c968a] break-all mb-4">{storeUrl}</p>
            <button data-testid="download-qr-btn" onClick={downloadQR} className="w-full h-12 rounded-2xl bg-[#1B4332] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform">
              <Download className="h-5 w-5" /> {t("settings.downloadPng")}
            </button>
            <p className="text-[11px] text-[#9c968a] mt-3">{t("settings.qrModalHint")}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function QrUpload({ svc, url, onUploaded }) {
  const { t } = useTranslation();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.url);
      toast.success(t("settings.qrAdded"));
    } catch (err) {
      toast.error(t("settings.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };
  return (
    <>
      <button
        type="button"
        data-testid={`paycfg-qr-${svc}`}
        onClick={() => fileRef.current?.click()}
        className="h-20 w-20 shrink-0 rounded-xl border-2 border-dashed border-[#E5E0D8] flex items-center justify-center text-[#9c968a] overflow-hidden bg-[#F9F6F0]"
      >
        {url ? <img src={fileUrl(url)} alt="QR" className="h-full w-full object-cover" /> : (uploading ? <span className="text-xs">…</span> : <QrCode className="h-7 w-7" />)}
      </button>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`paycfg-qr-input-${svc}`} />
    </>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[#5C574F] mb-1.5">{label}</label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9c968a] z-10" />}
        {children}
      </div>
    </div>
  );
}
